import { useMemo } from "react";
import { useStudioStore } from "@/store/studio-store";
import {
  deriveStudioCockpitTabStates,
  selectCockpitAttentionRoute,
  type StudioCockpitActivity,
  type StudioCockpitTab,
  type StudioCockpitTabState,
} from "./studio-cockpit-activity";
import { selectStudioCockpitActivities } from "./studio-cockpit-selectors";
import type { StudioAuthorityDomain } from "../studio-authority-workspaces";

export interface StudioCockpitModel {
  activities: StudioCockpitActivity[];
  states: Record<StudioCockpitTab, StudioCockpitTabState>;
  activeTab: StudioCockpitTab;
  activeAttention: StudioCockpitActivity | null;
}

function cockpitTabForDomain(
  domain: StudioAuthorityDomain | undefined,
): StudioCockpitTab {
  if (domain === "design" || domain === "guide") return domain;
  if (domain === "insights") return "status";
  // Deliberate default, not an omission: "primary" and undefined already
  // fell through to "app" before As-Is existed. "as-is" (G1 alternative A)
  // has no cockpit-attention lane (same reasoning as toCockpitTab in
  // artifact-mode-switcher.tsx and cockpitTabForDomain in
  // studio-cockpit-selectors.ts), so it joins that same default on purpose.
  return "app";
}

export function useStudioCockpit(): StudioCockpitModel {
  const activeRailItem = useStudioStore((state) => state.activeRailItem);
  const authorityDomain = useStudioStore(
    (state) => state.chatContext?.authorityDomain,
  );
  const channelDispatchStatus = useStudioStore(
    (state) => state.channelDispatchStatus,
  );
  const agenticUxTransaction = useStudioStore(
    (state) => state.agenticUxTransaction,
  );
  const validatorRunning = useStudioStore((state) => state.validatorRunning);
  const validatorError = useStudioStore((state) => state.validatorError);
  const validatorUnresolvedError = useStudioStore(
    (state) => state.validatorUnresolvedError,
  );
  const validatorLastRunAt = useStudioStore(
    (state) => state.validatorLastRunAt,
  );
  const validationReport = useStudioStore((state) => state.validationReport);
  const reviewedCockpitActivityIds = useStudioStore(
    (state) => state.reviewedCockpitActivityIds,
  );
  const openPencilImportActivity = useStudioStore(
    (state) => state.openPencilImportActivity,
  );

  return useMemo(() => {
    const activities = selectStudioCockpitActivities({
      activeRailItem,
      channelDispatchStatus,
      agenticUxTransaction,
      validatorRunning,
      validatorError,
      validatorUnresolvedError,
      validatorLastRunAt,
      validationReport,
      reviewedCockpitActivityIds,
      openPencilImportActivity,
    });
    const states = deriveStudioCockpitTabStates(activities);
    const activeTab = cockpitTabForDomain(authorityDomain);
    return {
      activities,
      states,
      activeTab,
      activeAttention: selectCockpitAttentionRoute(states[activeTab]),
    };
  }, [
    activeRailItem,
    agenticUxTransaction,
    authorityDomain,
    channelDispatchStatus,
    openPencilImportActivity,
    reviewedCockpitActivityIds,
    validationReport,
    validatorError,
    validatorUnresolvedError,
    validatorLastRunAt,
    validatorRunning,
  ]);
}
